import { supabase } from './supabase';
import { createCasePhasesFromTemplates } from './createCasePhases';
import { buildDefaultCasesPrescriptionFields, seedDefaultCasePrescription } from './defaultCasePrescription';

/** Champs repris de la version précédente (pack + contexte dossier, pas les fichiers). */
export const CASE_VERSION_COPY_FIELDS = [
  'patient_id',
  'user_profile_id',
  'priority',
  'case_type',
  'package',
  'prosthetist_id',
  'assigned_staff_id',
];

/**
 * Payload insert pour cases v(n+1) — démarrage assistant soumission.
 * @param {Record<string, unknown>} sourceCase
 * @param {number} nextVersion
 */
export function buildCaseVersionInsertRow(sourceCase, nextVersion) {
  const defaults = buildDefaultCasesPrescriptionFields();
  const row = {
    parent_case_id: sourceCase.id,
    version_number: nextVersion,
    status: 'in_progress',
    current_phase: 'submission',
    created_date: new Date().toISOString(),
    start_date: null,
    due_date: null,
    arch_to_treat: defaults.arch_to_treat,
    treatment_type: defaults.treatment_type,
    questions_notes: null,
    clinical_notes: null,
  };

  for (const key of CASE_VERSION_COPY_FIELDS) {
    if (sourceCase[key] !== undefined) row[key] = sourceCase[key];
  }

  if (sourceCase.title) {
    row.title = `${sourceCase.title} (v${nextVersion})`;
  }

  return row;
}

/**
 * Copie les lignes case_services vers le nouveau cas.
 * @param {Array<{ service_id: string, quantity?: number, unit_price?: number, currency_code?: string|null }>} serviceRows
 * @param {string} newCaseId
 */
export function buildCaseServiceCopyRows(serviceRows, newCaseId) {
  return (serviceRows || []).map((row) => ({
    case_id: newCaseId,
    service_id: row.service_id,
    quantity: row.quantity ?? 1,
    unit_price: row.unit_price,
    currency_code: row.currency_code ?? null,
  }));
}

async function decrementOwnerCaseCredit(ownerProfileId) {
  const { data: userProfile } = await supabase
    .from('user_profiles')
    .select('current_case_number')
    .eq('id', ownerProfileId)
    .single();

  if (!userProfile) return;

  const newCaseNumber = Math.max(0, parseInt(userProfile.current_case_number || 0, 10) - 1);
  await supabase
    .from('user_profiles')
    .update({ current_case_number: newCaseNumber.toString() })
    .eq('id', ownerProfileId);
}

/**
 * Archive v1 et crée v2 : pack + services repris, parcours assistant soumission (sans fichiers).
 * @param {{ sourceCaseId: string, ownerProfileId: string }} params
 * @returns {Promise<{ newCaseId: string, versionNumber: number, caseNumber?: string }>}
 */
export async function createCaseVersionFromSource({ sourceCaseId, ownerProfileId }) {
  const now = new Date().toISOString();

  const { data: sourceCase, error: sourceError } = await supabase
    .from('cases')
    .select('*')
    .eq('id', sourceCaseId)
    .single();

  if (sourceError) throw sourceError;
  if (!sourceCase) throw new Error('Dossier source introuvable.');
  if (sourceCase.status === 'cancelled') {
    throw new Error('Une commande annulée ne peut pas créer une nouvelle conception.');
  }
  if (sourceCase.status === 'archived') {
    throw new Error('Ce dossier est déjà archivé.');
  }

  const { data: serviceRows, error: servicesError } = await supabase
    .from('case_services')
    .select('service_id, quantity, unit_price, currency_code')
    .eq('case_id', sourceCaseId);

  if (servicesError) throw servicesError;

  const nextVersion = (sourceCase.version_number || 1) + 1;

  const { error: archiveError } = await supabase
    .from('cases')
    .update({
      status: 'archived',
      archived_at: now,
      updated_at: now,
    })
    .eq('id', sourceCaseId);

  if (archiveError) throw archiveError;

  const insertRow = buildCaseVersionInsertRow(sourceCase, nextVersion);

  const { data: newCase, error: insertError } = await supabase
    .from('cases')
    .insert([insertRow])
    .select('id, case_number, version_number')
    .single();

  if (insertError) throw insertError;

  const newCaseId = newCase.id;
  const ownerId = ownerProfileId || sourceCase.user_profile_id;

  await seedDefaultCasePrescription(supabase, newCaseId);

  const copiedServices = buildCaseServiceCopyRows(serviceRows, newCaseId);
  if (copiedServices.length) {
    const { error: serviceInsertError } = await supabase
      .from('case_services')
      .insert(copiedServices);
    if (serviceInsertError) throw serviceInsertError;
  }

  const { error: templateError } = await createCasePhasesFromTemplates(newCaseId, ownerId);
  if (templateError) throw templateError;

  await supabase
    .from('patients')
    .update({
      active_cases: 1,
      last_visit: now,
    })
    .eq('id', sourceCase.patient_id);

  await decrementOwnerCaseCredit(ownerId);

  return {
    newCaseId,
    versionNumber: newCase.version_number ?? nextVersion,
    caseNumber: newCase.case_number,
  };
}
